/** XeAgents.ai**/
import { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { spokenValueProposition } from "@shared/spoken-pitch";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

import { TemplateHeader } from "@/components/template-customize/TemplateHeader";
import { BasicSettings } from "@/components/template-customize/BasicSettings";
import { CompanyProductDetails } from "@/components/template-customize/CompanyProductDetails";
import { SalesContent } from "@/components/template-customize/SalesContent";
import { CallInstructions } from "@/components/template-customize/CallInstructions";
import { SaveTemplateButton } from "@/components/template-customize/SaveTemplateButton";
import { ExtractionStatus, TemplatePreview, TemplateTips } from "@/components/template-customize/Sidebar";
import { useTemplateCustomizer } from "@/components/template-customize/useTemplateCustomizer";
import { describeStep2Gaps, step2Gaps } from "@/components/template-customize/rules";
import { icpForRequest, type FlowTemplate, type SalesContentField, type TemplateVariable } from "@/components/template-customize/types";

/**
 * Turn a template into a script that speaks about THIS business.
 *
 * Four steps, and the shortest path through them is a URL: read the site, and
 * everything below Step 2 arrives filled in for review. Typing it all in still
 * works and always did - it is just no longer the only way, which is why the old
 * version of this page ended with most flows shipping the template's generic
 * copy.
 *
 * The rule the whole page is built around: AI fills it in, the user owns it. Any
 * field they touch is marked as theirs, is never overwritten by a re-extract,
 * and takes a confirmation before a regeneration can replace it.
 */

/** What the AI-authored (non-template) path collects. Mirrors the server's own default set. */
const CUSTOM_VARIABLES: TemplateVariable[] = [
  { key: "company_name", description: "Your company name", example: "Acme Corp", hasFallback: true },
  { key: "product_name", description: "Your product or service name", example: "Widget Pro", hasFallback: true },
  { key: "value_proposition", description: "Main value proposition", example: "Increase efficiency by 80%", hasFallback: true },
  { key: "industry", description: "Your target industry", example: "Technology", hasFallback: true },
  { key: "use_case", description: "Primary use case for the flow", example: "Sales outreach", hasFallback: true },
  { key: "agent_name", description: "AI agent's name", example: "Sarah", fromAgent: true },
  { key: "transfer_number", description: "Human handoff phone number", example: "+1234567890", fromAgent: true },
];

const ALL_SALES_FIELDS: SalesContentField[] = ['valueProposition', 'keyFeatures', 'objections'];

export default function FlowTemplateCustomizePage() {
  const { toast } = useToast();
  const params = useParams<{ templateId?: string; id?: string }>();
  const [, setLocation] = useLocation();
  const [saved, setSaved] = useState(false);

  // Editing a flow that was made from a template: the page reopens with the answers it was made with
  const editingFlowId = params.id;
  const { data: editedFlow, isLoading: flowLoading } = useQuery<any>({
    queryKey: [`/api/flow-automation/flows/${editingFlowId}`],
    enabled: Boolean(editingFlowId),
  });
  const savedTemplate = editedFlow?.executionConfig?.template;
  const templateId = editingFlowId ? savedTemplate?.templateId : params.templateId;

  const isCustomTemplate = templateId === "custom";

  const { data: templates = [], isLoading } = useQuery<FlowTemplate[]>({
    queryKey: ["/api/flow-automation/flow-templates"],
    enabled: !isCustomTemplate,
  });

  const template = isCustomTemplate ? null : templates.find(entry => entry.id === templateId) ?? null;

  const customizer = useTemplateCustomizer({
    template,
    isCustomTemplate,
    customVariables: CUSTOM_VARIABLES,
  });

  // Fill the page once with what the flow was saved with
  const hydrated = useRef(false);
  useEffect(() => {
    if (!editingFlowId || hydrated.current || !editedFlow || !savedTemplate || !template) return;
    hydrated.current = true;
    customizer.hydrate({
      name: editedFlow.name,
      language: savedTemplate.language,
      flowVariables: savedTemplate.flowVariables,
      customInstructions: savedTemplate.customInstructions,
      salesContent: savedTemplate.salesContent,
      icp: savedTemplate.icp,
      sourceDocument: savedTemplate.sourceDocument,
    });
  }, [editingFlowId, editedFlow, savedTemplate, template, customizer]);

  // A flow with no saved template (built by hand or by AI) has nothing to reopen here: use the visual builder
  useEffect(() => {
    if (editingFlowId && !flowLoading && editedFlow && !savedTemplate) setLocation(`/app/flows/${editingFlowId}`);
  }, [editingFlowId, flowLoading, editedFlow, savedTemplate, setLocation]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const instructions = customizer.instructions.value.trim() || undefined;
      const salesContent = {
        valueProposition: customizer.salesContent.valueProposition.value.trim(),
        keyFeatures: customizer.salesContent.keyFeatures.value.trim(),
        objections: customizer.salesContent.objections.value.trim(),
      };

      if (isCustomTemplate) {
        const response = await apiRequest("POST", "/api/flow-automation/generate-custom-flow", {
          name: customizer.flowName,
          language: customizer.language,
          companyDetails: customizer.variablesToSend,
          companyDescription: customizer.sourceText,
          customInstructions: instructions,
          salesContent,
        });
        return response.json();
      }

      const response = await apiRequest(
        editingFlowId ? "PUT" : "POST",
        editingFlowId
          ? `/api/flow-automation/flows/${editingFlowId}/template`
          : `/api/flow-automation/flow-templates/${templateId}/clone`,
        {
          name: customizer.flowName,
          language: customizer.language,
          flowVariables: customizer.variablesToSend,
          customInstructions: instructions,
          // Who is being called: the server rewrites the script's wording for
          // this industry rather than leaving the generic template copy.
          icp: icpForRequest(customizer.icp),
          // Features and objections have no template token to land in, so the
          // server folds them into the instructions the flow editor applies.
          salesContent,
          // What was read from the website or document: the script is rewritten from it too, not only from
          // the three boxes drafted from it.
          sourceDocument: customizer.sourceText.trim() ? customizer.sourceText.trim().slice(0, 12000) : undefined,
        },
      );
      return response.json();
    },
    onSuccess: (data) => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ["/api/flow-automation/flows"] });
      if (editingFlowId) {
        queryClient.invalidateQueries({ queryKey: [`/api/flow-automation/flows/${editingFlowId}`] });
        toast({
          title: "Flow updated",
          description: "The script was rewritten from these answers. Agents using this flow pick it up on their next call.",
        });
        return;
      }
      toast({
        title: "Template saved successfully",
        description: "Opening it now so you can review the full script.",
      });
      setLocation(`/app/flows/${data.id}`);
    },
    onError: (error: any) => {
      toast({
        title: "Could not save this template",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    customizer.markSubmitted();
    if (!customizer.isValid) {
      // The fields say which and why; this only points at them, because on a
      // four-step form the first invalid one is often off-screen.
      const firstInvalid = Object.keys(customizer.errors)[0];
      document.querySelector(`[data-testid="field-${firstInvalid}"], #${CSS.escape(firstInvalid)}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast({
        title: "Some required fields are still empty",
        description: "They're marked below — the agent speaks every one of them.",
        variant: "destructive",
      });
      return;
    }
    saveMutation.mutate();
  };

  if ((!isCustomTemplate && isLoading) || (editingFlowId && flowLoading)) {
    return (
      <div className="flex h-64 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isCustomTemplate && !template) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4 p-6">
        <p className="text-muted-foreground">Template not found</p>
        <Button variant="outline" onClick={() => setLocation("/app/flows/templates")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Templates
        </Button>
      </div>
    );
  }

  const templateName = isCustomTemplate ? "Custom" : template?.name ?? "Template";
  const invalidCount = Object.keys(customizer.errors).length;
  const canSave = customizer.isValid && !saveMutation.isPending;

  // Regenerating reads the website text when there is one, and otherwise what
  // has been typed into Step 2 - somebody who named their company and product
  // has told us enough to draft from.
  const generationSource = customizer.sourceText.trim() ||
    [customizer.value('company_name'), customizer.value('product_name'), customizer.value('target_audience')]
      .filter(Boolean).join("\n");
  const hasSource = customizer.usingWebsite || generationSource.trim().length >= 40;

  // The button waits until Step 2 is complete. The hint beside it always says
  // what for: a disabled button with no reason next to it is the thing people
  // file bugs about.
  const gaps = step2Gaps(customizer.errors);
  const canGenerate = hasSource && gaps.length === 0;
  // Names the fields rather than counting them: a dropdown nobody has touched
  // carries no error yet, so "3 fields marked above" pointed at nothing marked.
  const generateHint = gaps.length > 0
    ? `Fill in ${describeStep2Gaps(customizer.errors)} — they become the brief for the whole script.`
    : !hasSource
      ? "Add your website address, or fill in the company and product above, first."
      : customizer.usingWebsite
        ? "Reads your website and writes your sales content from it — 150-250 words you can edit."
        : "Writes your sales content from the details above — 150-250 words you can edit.";

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-0">
      <TemplateHeader
        templateName={templateName}
        onSave={handleSave}
        canSave={canSave}
        saving={saveMutation.isPending}
        saved={saved}
        invalidCount={invalidCount}
      />

      <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
        {editingFlowId && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/40 p-4" data-testid="banner-edit-template-flow">
            <p className="text-sm text-muted-foreground">
              You are editing a flow made from this template. Saving rewrites its script from the answers below, so changes made
              node by node in the visual builder are replaced.
            </p>
            <Button variant="outline" size="sm" onClick={() => setLocation(`/app/flows/${editingFlowId}`)} data-testid="button-open-visual-builder">
              Open visual builder
            </Button>
          </div>
        )}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start xl:grid-cols-[minmax(0,1fr)_400px]">
          {/* Form first in the DOM: it is what the page is for, and on mobile it
              must come before the sidebar without a CSS order trick. */}
          <div className="space-y-6">
            <BasicSettings
              name={customizer.flowName}
              onNameChange={customizer.setFlowName}
              onNameBlur={() => customizer.touch('flowName')}
              nameError={customizer.showError('flowName')}
              language={customizer.language}
              onLanguageChange={customizer.setLanguage}
            />

            <CompanyProductDetails
              websiteUrl={customizer.websiteUrl}
              onWebsiteUrlChange={customizer.setWebsiteUrl}
              extraction={customizer.extraction}
              analysisSteps={customizer.analysisSteps}
              onChooseProduct={customizer.chooseProduct}
              raw={customizer.raw}
              onValueChange={customizer.setVariable}
              onBlur={customizer.touch}
              error={customizer.showError}
              audienceError={customizer.showAudienceError}
              icp={customizer.icp}
              onIcpChange={customizer.setIcpField}
              audienceLine={customizer.audienceLine}
              icpStale={customizer.icpStale}
              onGenerate={customizer.generateEverything}
              canGenerate={canGenerate}
              generating={customizer.busyFields.length > 0 || customizer.extraction.status === 'analyzing'}
              hasContent={Object.values(customizer.salesContent).some(f => f.value.trim())}
              generateHint={generateHint}
            />

            <SalesContent
              content={customizer.salesContent}
              onChange={customizer.setSalesField}
              onRewrite={customizer.rewrite}
              onGenerateAll={() => customizer.generateSales(ALL_SALES_FIELDS, generationSource)}
              error={customizer.showError}
              // hasSource, not canGenerate: these are per-field rewrites the user
              // steers with their own instruction, so they only need something to
              // write from. The bulk button in Step 2 is the one that waits for the
              // whole brief, because it overwrites all three fields at once.
              canGenerate={hasSource}
              generating={customizer.busyFields.length > 0}
              extraVariables={customizer.extraVariables}
              raw={customizer.raw}
              onValueChange={customizer.setVariable}
              onBlur={customizer.touch}
              variableOrigins={customizer.variableOrigins}
              onRewriteVariable={customizer.rewriteVariable}
            />

            <CallInstructions
              state={customizer.instructions}
              onChange={(value) => customizer.setInstructions({ value, origin: 'edited' })}
              onSuggest={() => customizer.suggestInstructions(generationSource, true)}
              canSuggest={hasSource}
              staleForAudience={customizer.icpStale && customizer.instructions.origin === 'edited'}
            />
          </div>

          <aside className="space-y-4 lg:sticky lg:top-28">
            <ExtractionStatus
              stage={customizer.extraction}
              onReExtract={() => customizer.extractFromWebsite()}
            />
            <TemplatePreview
              name={customizer.flowName}
              openingLine={template?.openingLine}
              scriptPreview={template?.scriptPreview}
              values={{
                ...customizer.variables,
                // Only the opening sentence is spoken (the flow trims it the same way when it is saved), so the
                // preview shows that and not the 100-150 words that sit behind it.
                value_proposition: spokenValueProposition(
                  customizer.variables.value_proposition || customizer.salesContent.valueProposition.value,
                ),
              }}
            />
            <TemplateTips />
          </aside>
        </div>
      </div>

      {/* Mobile: the one action, where a thumb is. */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t bg-background/95 p-3 backdrop-blur sm:hidden">
        <SaveTemplateButton
          onSave={handleSave}
          disabled={!canSave}
          saving={saveMutation.isPending}
          saved={saved}
          className="w-full"
          testId="button-save-template-mobile"
        />
        {!canSave && !saveMutation.isPending && invalidCount > 0 && (
          <p className="mt-1 text-center text-[11px] text-muted-foreground">
            {invalidCount} required field{invalidCount === 1 ? "" : "s"} still to fill
          </p>
        )}
      </div>
    </div>
  );
}
